import { useActiveTabContext } from '@/context/ActiveTabContext';
import { useTabLocation } from '@/context/TabLocationContext';
import { useTabsContextSafe } from '@/context/TabsContext';
import { ROOT_PANE_ID } from '@/context/TabsContext/reducer';
import type { NavigateOptions as TabNavigateOptions } from '@/context/TabsContext/types';
import { useCallback } from 'react';
import {
  useNavigate as useReactRouterNavigate,
  type NavigateOptions as RouterNavigateOptions,
  type To,
} from 'react-router-dom';

type AppNavigateOptions = RouterNavigateOptions & {
  openInNewTab?: boolean;
  title?: string;
  tabParamName?: string;
  syncBrowserUrl?: boolean;
  allowInactiveTab?: boolean;
};

type UseAppNavigateOptions = {
  interceptBareTabKey?: boolean;
};

export type AppNavigate = (target: To | number, options?: AppNavigateOptions) => void;

const DEFAULT_TAB_PARAM_NAME = 'tab';

function isExternalUrl(target: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(target);
}

function isBareTabKey(target: string): boolean {
  return (
    target.length > 0
    && !target.startsWith('/')
    && !target.startsWith('.')
    && !target.startsWith('?')
    && !target.startsWith('#')
    && !target.includes('/')
    && !target.includes('?')
    && !target.includes(':')
  );
}

function normalizeSearch(search: string): string {
  if (!search) return '';
  return search.startsWith('?') ? search : `?${search}`;
}

function toBrowserUrl(target: To): string {
  if (typeof target === 'string') {
    if (isExternalUrl(target)) return target;
    return `${window.location.origin}${target.startsWith('/') ? target : `/${target}`}`;
  }

  const pathname = target.pathname || window.location.pathname;
  const search = normalizeSearch(target.search || '');
  const hash = target.hash || '';
  return `${window.location.origin}${pathname}${search}${hash}`;
}

function getTargetParts(target: To, fallbackPathname: string, fallbackSearch: string) {
  if (typeof target === 'string') {
    if (target.startsWith('?')) {
      const [search, hash = ''] = target.split('#');
      return {
        pathname: fallbackPathname,
        search: normalizeSearch(search),
        hash: hash ? `#${hash}` : '',
        isExternal: false,
      };
    }

    if (target.startsWith('#')) {
      return {
        pathname: fallbackPathname,
        search: fallbackSearch,
        hash: target,
        isExternal: false,
      };
    }

    const url = new URL(target, window.location.origin);
    return {
      pathname: url.pathname,
      search: url.search,
      hash: url.hash,
      isExternal: isExternalUrl(target),
    };
  }

  return {
    pathname: target.pathname || fallbackPathname,
    search: normalizeSearch(target.search || fallbackSearch),
    hash: target.hash || '',
    isExternal: false,
  };
}

function getSearchWithTab(currentSearch: string, tabParamName: string, tabKey: string): string {
  const params = new URLSearchParams(currentSearch);
  params.set(tabParamName, tabKey);
  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : '';
}

export function useAppNavigate(options: UseAppNavigateOptions = {}): AppNavigate {
  const { interceptBareTabKey = true } = options;
  const routerNavigate = useReactRouterNavigate();
  const tabLocation = useTabLocation();
  const activeTab = useActiveTabContext();
  const tabsContext = useTabsContextSafe();

  return useCallback<AppNavigate>(
    (target, navigateOptions) => {
      const isSplitView = Boolean(tabLocation && tabsContext && tabsContext.state.panes.length > 1);
      const isPaneLocalNavigation = isSplitView && Boolean(tabLocation?.paneId && tabLocation.paneId !== ROOT_PANE_ID);

      if (tabLocation && !activeTab.isActive && !navigateOptions?.allowInactiveTab) {
        console.warn(`[KeepAlive] Blocked app navigation from background tab: ${tabLocation.key}`, target);
        return;
      }

      if (typeof target === 'number') {
        if (isPaneLocalNavigation && tabLocation && tabsContext) {
          if (target < 0) {
            tabsContext.goBack(tabLocation.paneId);
          } else if (target > 0) {
            tabsContext.goForward(tabLocation.paneId);
          }
          return;
        }

        routerNavigate(target);
        return;
      }

      if (navigateOptions?.openInNewTab) {
        window.open(toBrowserUrl(target), '_blank', 'noopener,noreferrer');
        return;
      }

      const tabParamName = navigateOptions?.tabParamName || DEFAULT_TAB_PARAM_NAME;
      const fallbackPathname = tabLocation?.pathname || window.location.pathname;
      const fallbackSearch = tabLocation?.search || window.location.search;

      if (typeof target === 'string' && interceptBareTabKey && isBareTabKey(target)) {
        const search = getSearchWithTab(fallbackSearch, tabParamName, target);

        if (tabLocation && tabsContext) {
          tabsContext.navigateInTab(fallbackPathname, {
            search,
            replace: navigateOptions?.replace ?? true,
            state: navigateOptions?.state,
            title: navigateOptions?.title,
            paneId: tabLocation.paneId,
            syncBrowserUrl: navigateOptions?.syncBrowserUrl ?? (activeTab.isActive && !isPaneLocalNavigation),
          });
          return;
        }

        routerNavigate({ pathname: fallbackPathname, search }, { replace: navigateOptions?.replace ?? true, state: navigateOptions?.state });
        return;
      }

      const targetParts = getTargetParts(target, fallbackPathname, fallbackSearch);
      if (targetParts.isExternal) {
        window.open(toBrowserUrl(target), '_blank', 'noopener,noreferrer');
        return;
      }

      if (tabLocation && tabsContext) {
        const tabOptions: TabNavigateOptions = {
          search: targetParts.search,
          replace: navigateOptions?.replace ?? isPaneLocalNavigation,
          state: navigateOptions?.state,
          title: navigateOptions?.title,
          paneId: tabLocation.paneId,
          syncBrowserUrl: navigateOptions?.syncBrowserUrl ?? (activeTab.isActive && !isPaneLocalNavigation),
        };

        tabsContext.navigateInTab(targetParts.pathname, tabOptions);
        return;
      }

      routerNavigate(target, navigateOptions);
    },
    [activeTab.isActive, interceptBareTabKey, routerNavigate, tabLocation, tabsContext],
  );
}

export default useAppNavigate;
